import type { Metadata } from "next";

export {
  BRAND_HOME_ARIA_LABEL,
  BRAND_LOGO_ALT,
  BRAND_LOGO_TITLE,
} from "@v1/ui/brand-logo-mark";

/** Product brand string used in titles and image alt text. */
export const BRAND_NAME: string = "hausverwalter.ai";

/** Absolute site origin — same source and fallback as the root `metadataBase`. */
export const SITE_URL: string =
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://app.hausverwalter.ai";

/** Primary SEO description — natural keyword coverage, no stuffing. */
export const SITE_DESCRIPTION: string =
  "Hausverwaltungssoftware mit Vorgängen, ERP und Dokumenten in einem System. Deine KI, etwa Claude, ChatGPT oder Langdock, bedient alles, ohne Login.";

/** Category term the landing page targets in search. Metadata, alt text and visible copy may use it. */
export const AI_CATEGORY_TERM: string = "KI für Hausverwaltungen";

/** Alt text for the logo lockups on the landing page. */
export const MARKETING_LOGO_ALT: string = `hausverwalter.ai — ${AI_CATEGORY_TERM}`;

/**
 * Accessible name of the interactive product window on the landing page. The
 * window is a live mock of the app, so screen readers (and crawlers) get the
 * description a screenshot would otherwise carry in its alt text.
 */
export const MARKETING_PRODUCT_WINDOW_LABEL: string = `hausverwalter.ai in Aktion — ${AI_CATEGORY_TERM}: Inbox, Dokumente, Telefonanlage, Kundenportal und KI-Agenten in einer Oberfläche`;

export const MEET_DANIEL_ALT: string =
  "Online hybride Eigentümerversammlung: Hausverwalter führt die ETV in hausverwalter.ai";

export const MEET_DANIEL_TITLE: string =
  "Online-Eigentümerversammlung für Hausverwaltungen — hausverwalter.ai";

export const MEET_WOLFGANG_ALT: string =
  "Eigentümer im Kundenportal bei der Online-Eigentümerversammlung über hausverwalter.ai";

export const MEET_WOLFGANG_TITLE: string =
  "ETV Software Hausverwaltung — Kundenportal";

export const OPEN_GRAPH_IMAGE_PATH: string = "/brand/logo-light.png";

/** Profiles the website footer links; schema.org `sameAs`. */
export const BRAND_PROFILE_URLS: readonly string[] = [
  "https://www.youtube.com/@hausverwalter-ai",
  "https://open.spotify.com/show/033k4UZU6bCuhiABtyxxFx",
];

/** Shared Open Graph + Twitter card defaults for marketing pages. */
export function buildMarketingOpenGraph(
  title: string,
  description: string,
): Metadata["openGraph"] {
  return {
    title,
    description,
    siteName: BRAND_NAME,
    locale: "de_DE",
    type: "website",
    images: [
      {
        url: OPEN_GRAPH_IMAGE_PATH,
        alt: MARKETING_LOGO_ALT,
      },
    ],
  };
}

export function buildMarketingTwitter(
  title: string,
  description: string,
): Metadata["twitter"] {
  return {
    card: "summary_large_image",
    title,
    description,
    images: [OPEN_GRAPH_IMAGE_PATH],
  };
}

/** Base metadata fields reused across public marketing routes. */
export function buildMarketingMetadata(
  title: string,
  description: string = SITE_DESCRIPTION,
): Metadata {
  return {
    title,
    description,
    openGraph: buildMarketingOpenGraph(title, description),
    twitter: buildMarketingTwitter(title, description),
  };
}

/** Indexed website pages. Website sitemap lists them; each sets its canonical. */
export const INDEXED_WEBSITE_PATHS = [
  "/",
  "/preise",
  "/demo",
  "/features/inbox",
  "/features/phone",
  "/features/ai-answering-service",
  "/features/mailings",
  "/features/team-chat",
  "/features/documents",
  "/features/e-signature",
  "/features/audit-proof-archive",
  "/features/invoice-approval",
  "/features/customer-portal",
  "/features/owners-meetings",
  "/features/search",
  "/features/forms",
  "/features/calendar",
  "/features/booking",
  "/features/properties",
  "/features/contacts",
  "/features/leases",
  "/features/meters",
  "/features/keys",
  "/features/maintenance",
  "/features/bank-connection",
  "/features/rent-collection",
  "/features/dunning",
  "/features/budget-plan",
  "/features/annual-statement",
  "/features/service-charges",
  "/features/rent-increases",
  "/features/tasks",
  "/features/time-tracking",
  "/features/knowledge",
  "/features/clips",
  "/features/learning",
  "/features/help-center",
  "/features/api",
  "/features/ai-agents",
  "/features/customer-chat",
  "/features/ai-calls",
  "/features/document-agent",
  "/features/invoice-agent",
  "/integrations/impower",
  "/integrations/domus",
  "/integrations/immoware24",
  "/integrations/powerhaus",
  "/compare",
  "/compare/casavi",
  "/compare/impower",
  "/compare/facilioo",
  "/compare/idwell",
  "/compare/domus",
  "/compare/immoware24",
  "/compare/powerhaus",
  "/compare/easimo",
  "/careers",
  "/careers/en",
  "/careers/engineering",
  "/partner",
  "/migration-service",
  "/impressum",
  "/datenschutz",
] as const;

export type IndexedWebsitePath = (typeof INDEXED_WEBSITE_PATHS)[number];

/** Website page metadata plus canonical, so query and host variants fold into one URL. */
export function buildWebsitePageMetadata(
  path: IndexedWebsitePath,
  title: string,
  description: string,
): Metadata {
  return {
    ...buildMarketingMetadata(title, description),
    alternates: { canonical: path },
  };
}

/**
 * German marketing default for {@link orgLogoTitle}. Booking and brand pages
 * keep this suffix unless a caller passes a translated `portalProductName`.
 */
// i18n-exempt: German marketing SEO default for booking/brand pages; portal callers pass a translated suffix.
export const DEFAULT_PORTAL_PRODUCT_NAME: string =
  "Kundenportal Hausverwaltung";

/** Alt text for a tenant / customer org logo on public surfaces. */
export function orgLogoAlt(orgName: string): string {
  return `Logo ${orgName}`;
}

/** Title for a tenant org logo on booking / portal pages. */
export function orgLogoTitle(
  orgName: string,
  portalProductName: string = DEFAULT_PORTAL_PRODUCT_NAME,
): string {
  return `${orgName} — ${portalProductName}`;
}

/** Alt text for a publicly shared file image preview. */
export function sharedFilePreviewAlt(fileName: string): string {
  return `Vorschau der geteilten Datei ${fileName}`;
}

/** Title for a publicly shared file image preview. */
export function sharedFilePreviewTitle(fileName: string): string {
  return `Geteilte Datei ${fileName} — hausverwalter.ai DMS`;
}
